import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IShipmentStatus } from '../api/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 16px;
    border-radius: 12px;
    gap: 16px;
    margin: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  background-color: #172A2A;
  border-radius: 8px;
  border: 1px solid #284640;
`;

const ProductImage = styled.div`
  width: 60px;
  height: 60px;
  background-color: transparent;
  border-radius: 6px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
`;

const ProductName = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  margin: 0;
  color: #FFFFFF;
`;

const ProductModel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #609F94;
`;

const ProductSize = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #838D90;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
`;

const Select = styled.select`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #284640;
  background-color: #172A2A;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  option {
    background-color: #172A2A;
    color: #FFFFFF;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px; /* Предотвращает zoom на iOS */
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 16px;
    width: 100%;
  }
  
  ${props => props.variant === 'primary' ? `
    background-color: #24BA7D;
    color: #FFFFFF;
    
    &:hover {
      background-color: #1E9A66;
    }
    
    &:disabled {
      background-color: #284640;
      color: #838D90;
      cursor: not-allowed;
    }
  ` : `
    background-color: transparent;
    color: #838D90;
    border: 1px solid #284640;
    
    &:hover {
      background-color: #172A2A;
      color: #FFFFFF;
    }
  `}
`;

interface ProductData {
  id: number;
  name: string;
  model: string;
  size: string;
  imageUrl?: string;
}

interface AddToShipmentModalProps {
  isOpen: boolean;
  product: ProductData | null;
  statuses: IShipmentStatus[];
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (sneakerId: number, statusId: number) => Promise<void>;
}

const AddToShipmentModal: React.FC<AddToShipmentModalProps> = ({
  isOpen,
  product,
  statuses,
  onClose,
  onSuccess,
  onSubmit
}) => {
  const [selectedStatusId, setSelectedStatusId] = useState('');
  const [loading, setLoading] = useState(false);

  // Сброс формы при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      setSelectedStatusId('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!product || !selectedStatusId) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(product.id, parseInt(selectedStatusId));
      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Ошибка при добавлении товара в отгрузку:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatusId('');
    setLoading(false);
    onClose();
  };

  const isFormValid = product && selectedStatusId;

  if (!isOpen || !product) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Добавить в отгрузку</ModalTitle>
        </ModalHeader>

        <ProductInfo>
          {product.imageUrl && (
            <ProductImage style={{ backgroundImage: `url(${product.imageUrl})` }} />
          )}
          <ProductDetails>
            <ProductName>{product.name}</ProductName>
            <ProductModel>{product.model}</ProductModel>
            <ProductSize>Размер: {product.size}</ProductSize>
          </ProductDetails>
        </ProductInfo>

        <FormField>
          <Label>Статус отгрузки</Label>
          <Select
            value={selectedStatusId}
            onChange={(e) => setSelectedStatusId(e.target.value)}
            disabled={loading}
          >
            <option value="">Выберите статус</option>
            {Array.isArray(statuses) && statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.status}
              </option>
            ))}
          </Select>
        </FormField>

        <ButtonContainer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? 'Добавление...' : 'Добавить в отгрузку'}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddToShipmentModal;
